import type { PartnerDriverMode } from "../../lib/partnerDrivers";
import { useI18n } from "../../i18n";

interface DriverModeFieldProps {
  value: PartnerDriverMode;
  onChange: (mode: PartnerDriverMode) => void;
  disabled?: boolean;
}

/**
 * Driver type (engagement) selector — Contractor (paid) vs Staff (own).
 * Used in the create-driver modal and in the driver profile edit form.
 */
export function DriverModeField({
  value,
  onChange,
  disabled,
}: DriverModeFieldProps) {
  const { t } = useI18n();
  const options: {
    mode: PartnerDriverMode;
    labelKey: "drivers.mode.paid" | "drivers.mode.own";
    hintKey: "drivers.modePaidHint" | "drivers.modeOwnHint";
  }[] = [
    {
      mode: "paid",
      labelKey: "drivers.mode.paid",
      hintKey: "drivers.modePaidHint",
    },
    {
      mode: "own",
      labelKey: "drivers.mode.own",
      hintKey: "drivers.modeOwnHint",
    },
  ];
  const active =
    options.find((o) => o.mode === value) ??
    ({
      mode: "paid",
      labelKey: "drivers.mode.paid",
      hintKey: "drivers.modePaidHint",
    } as const);

  return (
    <div className="block space-y-2">
      <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
        {t("drivers.modeLabel")}
      </span>
      <div
        className="grid grid-cols-2 gap-2"
        role="radiogroup"
        aria-label={t("drivers.modeLabel")}
      >
        {options.map((option) => (
          <button
            key={option.mode}
            type="button"
            role="radio"
            aria-checked={value === option.mode}
            disabled={disabled}
            className={`rounded-xl border px-3 py-2.5 text-start transition-colors ${
              value === option.mode
                ? "border-[var(--accent)] bg-[var(--accent-light)]"
                : "border-[var(--border)]"
            } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
            onClick={() => onChange(option.mode)}
          >
            <span className="block text-sm font-bold text-[var(--text-primary)]">
              {t(option.labelKey)}
            </span>
          </button>
        ))}
      </div>
      <p className="text-xs text-[var(--text-muted)]">{t(active.hintKey)}</p>
    </div>
  );
}
